import type { BlackjackRules } from '@casino/shared';
import { ApiError, apiCall } from '../api/client.js';
import { audio } from '../audio/AudioManager.js';
import { authenticate, AuthError } from '../discord/auth.js';
import { installPlatformAttributes, setOrientationLock } from '../discord/platform.js';
import { bootDiscord } from '../discord/sdk.js';
import { MissingEnvError } from '../env.js';
import { applyUserState, setScreen, startVisibilityRefresh, store } from '../state/store.js';
import { animate, DURATION, EASING, isReducedMotion, syncMotionAttribute } from './anim.js';
import { createBoot, type BootScreen } from './boot.js';
import { clear } from './dom.js';
import { createMenu, type MenuScreen } from './menu.js';
import { sessionGuard } from './sessionGuard.js';
import { applySettings } from './settings.js';
import { toast } from './toast.js';

/**
 * Montage de l'application et enchainement des ecrans.
 *
 * Sequence, dans cet ordre strict :
 *   1. preferences + deverrouillage audio differe
 *   2. DiscordSDK.ready()                         (discord/sdk.ts)
 *   3. authorize -> /api/token -> jeton de session en memoire (discord/auth.ts)
 *   4. etat public autoritaire (/api/me) + rafraichissement au retour d'onglet
 *   5. menu
 * Toute rupture s'arrete sur un message, jamais sur une boucle.
 */

let mounted = false;

export function mountApp(host: HTMLElement): void {
  if (mounted) return;
  mounted = true;

  applySettings();
  syncMotionAttribute();
  // L'AudioContext ne sera cree qu'au premier geste : politique d'autoplay.
  audio.installUnlockListeners();

  clear(host);
  const boot = createBoot();
  host.appendChild(boot.root);

  void run(host, boot);
}

async function run(host: HTMLElement, boot: BootScreen): Promise<void> {
  boot.setStep('discord');

  const discord = await bootDiscord();

  if (!discord.ok) {
    switch (discord.reason) {
      case 'no-frame-id':
        // Cas normal quand on ouvre l'URL a la main : on informe, on n'alerte
        // pas, et surtout on ne relance rien en boucle.
        boot.fail(
          'Ouvre cette activité depuis Discord',
          'Salon Minuit est une activité Discord : lance-la depuis un salon vocal, via le menu Activités. Ouverte seule dans un navigateur, cette page ne peut pas t’authentifier.',
          { calm: true },
        );
        break;
      case 'missing-config':
        boot.fail('Configuration incomplète', discord.detail);
        break;
      default:
        boot.fail('Discord n’a pas répondu', discord.detail);
        break;
    }
    return;
  }

  const sdk = discord.sdk;
  installPlatformAttributes(sdk);
  // Echoue silencieusement sur desktop, c'est attendu.
  void setOrientationLock(sdk);

  boot.setStep('auth');

  try {
    const result = await authenticate(sdk, (stage) => {
      if (stage === 'exchanging') boot.setStep('auth');
    });

    boot.setStep('profile');
    applyUserState(result.user);

    // Rafraichissement autoritaire : cree le compte si /api/token ne l'a pas
    // fait et resynchronise la disponibilite du bonus quotidien.
    try {
      const me = await apiCall('/api/me', {});
      applyUserState(me.user);
    } catch {
      // /api/token nous a deja donne un etat exploitable : on continue.
    }

    // Filet leger : au retour sur l'onglet, on redemande l'etat au serveur (au
    // plus une fois par minute). Le solde reste decide par le serveur seul ;
    // le temps reel viendra du WebSocket des tables, pas d'un polling.
    startVisibilityRefresh(async () => {
      const fresh = await apiCall('/api/me', {});
      applyUserState(fresh.user);
    });
  } catch (error) {
    boot.fail(...describeFailure(error));
    return;
  }

  sessionGuard.start();
  await showMenu(host, boot);
}

function describeFailure(error: unknown): [string, string] {
  if (error instanceof MissingEnvError) {
    return ['Configuration incomplète', error.message];
  }
  if (error instanceof ApiError) {
    return ['Le serveur a refusé la connexion', `${error.userMessage} (code ${error.code})`];
  }
  if (error instanceof AuthError) {
    return ['Authentification impossible', error.message];
  }
  if (error instanceof Error) {
    return ['Démarrage interrompu', error.message];
  }
  return ['Démarrage interrompu', String(error)];
}

// ---------------------------------------------------------------------------
// Navigation entre ecrans
// ---------------------------------------------------------------------------

/**
 * Un seul ecran monte a la fois, et son `destroy` est toujours appele :
 * abonnements a l'etat, minuteries, canvas Phaser. C'est ce qui evite qu'un
 * aller-retour menu -> table laisse deux HUD abonnes au store.
 */
interface MountedScreen {
  readonly root: HTMLElement;
  readonly destroy: () => void;
}

let currentScreen: MountedScreen | null = null;
/** Jeton de navigation : une transition tardive ne peut pas ecraser la suivante. */
let navigationToken = 0;

async function swap(host: HTMLElement, next: MountedScreen): Promise<void> {
  const token = (navigationToken += 1);
  const previous = currentScreen;
  currentScreen = next;

  if (isReducedMotion()) {
    previous?.destroy();
    previous?.root.remove();
    if (token === navigationToken) host.appendChild(next.root);
    return;
  }

  audio.play('whoosh');
  if (previous) {
    const leaving = animate(
      previous.root,
      [
        { opacity: 1, transform: 'none', filter: 'blur(0px)' },
        { opacity: 0, transform: 'scale(1.02)', filter: 'blur(6px)' },
      ],
      { duration: DURATION.screen, easing: EASING.in },
    );
    if (leaving) {
      try {
        await leaving.finished;
      } catch {
        // Transition annulee : on poursuit quand meme.
      }
    }
    previous.destroy();
    previous.root.remove();
  }

  if (token !== navigationToken) {
    next.destroy();
    return;
  }

  host.appendChild(next.root);
  animate(
    next.root,
    [
      { opacity: 0, transform: 'translateY(12px)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: DURATION.screen, easing: EASING.out },
  );
}

async function showMenu(host: HTMLElement, boot: BootScreen | null): Promise<void> {
  const menu: MenuScreen = createMenu({
    onOpenGame: (id) => {
      if (id === 'slots') void showSlots(host);
      if (id === 'blackjack') void showLobby(host);
      if (id === 'roulette') void showRoulette(host);
    },
  });
  setScreen('menu');

  if (boot) {
    // Premier montage : c'est l'ecran de boot qui sort.
    currentScreen = { root: boot.root, destroy: () => boot.destroy() };
  }
  await swap(host, menu);
  announce();
}

/**
 * Table des machines a sous.
 *
 * L'ecran est charge a la demande : son module entraine avec lui le chunk
 * Phaser, qui ne doit pas peser sur le demarrage de l'activite.
 */
async function showRoulette(host: HTMLElement): Promise<void> {
  let screen: MountedScreen;
  try {
    const module = await import('./screens/roulette.js');
    screen = module.createRouletteScreen({ onBack: () => void showMenu(host, null) });
  } catch {
    toast('La table n’a pas pu être chargée.', { kind: 'error' });
    return;
  }
  setScreen('roulette');
  await swap(host, screen);
}

async function showSlots(host: HTMLElement): Promise<void> {
  let screen: MountedScreen;
  try {
    const module = await import('./screens/slots.js');
    screen = module.createSlotsScreen({ onBack: () => void showMenu(host, null) });
  } catch {
    toast('La table n’a pas pu être chargée.', { kind: 'error' });
    return;
  }
  setScreen('slots');
  await swap(host, screen);
}

/**
 * Salon des tables de blackjack, puis la table elle-meme.
 *
 * Les deux ecrans sont charges a la demande : ils entrainent avec eux le client
 * WebSocket et la feuille de style des tables, inutiles tant que le joueur ne
 * s'assoit pas.
 */
async function showLobby(host: HTMLElement): Promise<void> {
  let screen: MountedScreen;
  try {
    const module = await import('./screens/lobby.js');
    screen = module.createLobbyScreen({
      onBack: () => void showMenu(host, null),
      onOpenTable: (tableId, rules) => void showBlackjack(host, tableId, rules),
    });
  } catch {
    toast('Le salon n’a pas pu être chargé.', { kind: 'error' });
    return;
  }
  setScreen('lobby');
  await swap(host, screen);
}

async function showBlackjack(
  host: HTMLElement,
  tableId: string,
  rules: BlackjackRules | null,
): Promise<void> {
  let screen: MountedScreen;
  try {
    const module = await import('./screens/blackjack.js');
    screen = module.createBlackjackScreen({
      tableId,
      rules,
      onBack: () => void showLobby(host),
    });
  } catch {
    toast('La table n’a pas pu être chargée.', { kind: 'error' });
    return;
  }
  setScreen('blackjack');
  await swap(host, screen);
}

/** Trace de recette : l'etat d'ouverture, lisible dans la console Discord. */
function announce(): void {
  const user = store.state.user;
  if (!user) return;
  console.info(`Connecté en tant que ${user.username}, solde ${user.balance}`);
}
